import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCapabilities } from '../../components/admin/CapabilityProvider';
import NotAuthorized from '../../components/admin/NotAuthorized';
import { getAuditEvents } from '../../services/auditService';
import AdminPageHeader from '../../components/admin/ui/AdminPageHeader';
import AdminFilterBar from '../../components/admin/ui/AdminFilterBar';
import AdminTable, { AdminTableHeader, AdminTableRow, AdminTableCell, AdminTableHeaderCell } from '../../components/admin/ui/AdminTable';
import AdminButton from '../../components/admin/ui/AdminButton';
import AdminAlert from '../../components/admin/ui/AdminAlert';
import AdminEmptyState from '../../components/admin/ui/AdminEmptyState';
import AdminStatusBadge from '../../components/admin/ui/AdminStatusBadge';

export default function AuditPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { has } = useCapabilities();
  const canRead = has('admin:audit:read');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [events, setEvents] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());

  // Filters - initialize from URL params
  const [filters, setFilters] = useState({
    since: searchParams.get('since') || '',
    until: searchParams.get('until') || '',
    action: searchParams.get('action') || '',
    actionPrefix: searchParams.get('actionPrefix') || '',
    actions: searchParams.get('actions') || '',
    actor_id: searchParams.get('actor_id') || '',
    request_id: searchParams.get('request_id') || '',
    target_id: searchParams.get('target_id') || '',
    outcome: searchParams.get('outcome') || '',
    integration: searchParams.get('integration') || '',
    q: searchParams.get('q') || '',
  });
  const [timePreset, setTimePreset] = useState('24h');

  // Load events when component mounts or filters change
  useEffect(() => {
    if (canRead) {
      loadEvents();
    } else {
      setLoading(false);
    }
  }, [canRead]);

  // Apply time preset
  useEffect(() => {
    if (timePreset === '24h') {
      const until = new Date();
      const since = new Date(until.getTime() - 24 * 60 * 60 * 1000);
      setFilters(prev => ({
        ...prev,
        since: since.toISOString(),
        until: until.toISOString(),
      }));
    } else if (timePreset === '7d') {
      const until = new Date();
      const since = new Date(until.getTime() - 7 * 24 * 60 * 60 * 1000);
      setFilters(prev => ({
        ...prev,
        since: since.toISOString(),
        until: until.toISOString(),
      }));
    } else if (timePreset === '30d') {
      const until = new Date();
      const since = new Date(until.getTime() - 30 * 24 * 60 * 60 * 1000);
      setFilters(prev => ({
        ...prev,
        since: since.toISOString(),
        until: until.toISOString(),
      }));
    } else if (timePreset === 'all') {
      setFilters(prev => ({
        ...prev,
        since: '',
        until: '',
      }));
    }
  }, [timePreset]);

  const loadEvents = async (cursor = null) => {
    try {
      setLoading(true);
      setError(null);
      const params = {
        limit: 50,
        ...filters,
      };
      if (cursor) {
        params.cursor = cursor;
      }
      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null) {
          delete params[key];
        }
      });
      const response = await getAuditEvents(params);
      const items = response.items || [];
      setEvents(items);
      setNextCursor(response.nextCursor || null);
    } catch (err) {
      setError(err.message || 'Failed to load audit events');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleApplyFilters = () => {
    loadEvents();
  };

  const handleNextPage = () => {
    if (nextCursor) {
      loadEvents(nextCursor);
    }
  };

  const toggleRow = (eventId) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(eventId)) {
        next.delete(eventId);
      } else {
        next.add(eventId);
      }
      return next;
    });
  };

  const isActorUUID = (actorId) => {
    // Check if actor_id looks like a UUID (used in RBAC users)
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(actorId);
  };

  const getIntegrationCode = (event) => {
    // Check if target_id is an integration code, or if metadata has integration_code
    if (event.target_type === 'integration' && event.target_id) {
      return event.target_id;
    }
    if (event.metadata && event.metadata.integration_code) {
      return event.metadata.integration_code;
    }
    return null;
  };

  if (!canRead) {
    return <NotAuthorized requiredCapability="admin:audit:read" />;
  }

  if (loading && events.length === 0) {
    return <div className="admin-loading">Loading audit events...</div>;
  }

  if (error) {
    return (
      <div>
        <AdminAlert type="error" message={`Error: ${error}`} dismissible={false} />
        <AdminButton onClick={() => loadEvents()} ariaLabel="Retry loading audit events">
          Retry
        </AdminButton>
      </div>
    );
  }

  return (
    <div>
      <AdminPageHeader title="Audit Log Explorer" />

      {/* Preset Filter Buttons */}
      <div style={{ marginBottom: 'var(--spacing-4)', display: 'flex', gap: 'var(--spacing-2)', flexWrap: 'wrap' }}>
        <AdminButton
          variant="secondary"
          onClick={() => {
            const until = new Date();
            const since = new Date(until.getTime() - 7 * 24 * 60 * 60 * 1000);
            setFilters({
              since: since.toISOString(),
              until: until.toISOString(),
              action: '',
              actionPrefix: 'users.',
              actions: '',
              actor_id: '',
              request_id: '',
              target_id: '',
              outcome: '',
              integration: '',
              q: '',
            });
            setTimePreset('7d');
            loadEvents();
          }}
        >
          User Management
        </AdminButton>
        <AdminButton
          variant="secondary"
          onClick={() => {
            const until = new Date();
            const since = new Date(until.getTime() - 7 * 24 * 60 * 60 * 1000);
            setFilters({
              since: since.toISOString(),
              until: until.toISOString(),
              action: '',
              actionPrefix: 'auth.',
              actions: '',
              actor_id: '',
              request_id: '',
              target_id: '',
              outcome: '',
              integration: '',
              q: '',
            });
            setTimePreset('7d');
            loadEvents();
          }}
        >
          Auth
        </AdminButton>
        <AdminButton
          variant="secondary"
          onClick={() => {
            const until = new Date();
            const since = new Date(until.getTime() - 7 * 24 * 60 * 60 * 1000);
            setFilters({
              since: since.toISOString(),
              until: until.toISOString(),
              action: 'rbac.denied',
              actionPrefix: '',
              actions: '',
              actor_id: '',
              request_id: '',
              target_id: '',
              outcome: '',
              integration: '',
              q: '',
            });
            setTimePreset('7d');
            loadEvents();
          }}
        >
          Lockout Denials
        </AdminButton>
      </div>

      {/* Filter Bar */}
      <AdminFilterBar
        filters={
          <>
            <div className="admin-form-group">
              <label className="admin-form-label">Time Preset:</label>
              <select
                value={timePreset}
                onChange={(e) => setTimePreset(e.target.value)}
                className="admin-select"
              >
                <option value="24h">Last 24 hours</option>
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
                <option value="all">All time</option>
              </select>
            </div>

            <div className="admin-form-group">
              <label className="admin-form-label">Action:</label>
              <input
                type="text"
                value={filters.action}
                onChange={(e) => handleFilterChange('action', e.target.value)}
                placeholder="e.g., integration.sync.requested"
                className="admin-input"
              />
            </div>

            <div className="admin-form-group">
              <label className="admin-form-label">Actor ID:</label>
              <input
                type="text"
                value={filters.actor_id}
                onChange={(e) => handleFilterChange('actor_id', e.target.value)}
                placeholder="Filter by actor"
                className="admin-input"
              />
            </div>

            <div className="admin-form-group">
              <label className="admin-form-label">Target ID:</label>
              <input
                type="text"
                value={filters.target_id}
                onChange={(e) => handleFilterChange('target_id', e.target.value)}
                placeholder="Filter by target"
                className="admin-input"
              />
            </div>

            <div className="admin-form-group">
              <label className="admin-form-label">Request ID:</label>
              <input
                type="text"
                value={filters.request_id}
                onChange={(e) => handleFilterChange('request_id', e.target.value)}
                placeholder="Filter by request ID"
                className="admin-input"
              />
            </div>

            <div className="admin-form-group">
              <label className="admin-form-label">Outcome:</label>
              <select
                value={filters.outcome}
                onChange={(e) => handleFilterChange('outcome', e.target.value)}
                className="admin-select"
              >
                <option value="">All</option>
                <option value="success">Success</option>
                <option value="failure">Failure</option>
              </select>
            </div>

            <div className="admin-form-group">
              <label className="admin-form-label">Integration:</label>
              <input
                type="text"
                value={filters.integration}
                onChange={(e) => handleFilterChange('integration', e.target.value)}
                placeholder="e.g., hostaway"
                className="admin-input"
              />
            </div>

            <div className="admin-form-group">
              <label className="admin-form-label">Search:</label>
              <input
                type="text"
                value={filters.q}
                onChange={(e) => handleFilterChange('q', e.target.value)}
                placeholder="Search in target_id and metadata"
                className="admin-search-input"
              />
            </div>
          </>
        }
        actions={
          <AdminButton variant="primary" onClick={handleApplyFilters}>
            Apply Filters
          </AdminButton>
        }
      />

      {/* Events Table */}
      {events.length === 0 ? (
        <AdminEmptyState
          title="No audit events found"
          description="Try adjusting your filters to see more results."
        />
      ) : (
        <AdminTable>
          <AdminTableHeader>
            <AdminTableHeaderCell>Time</AdminTableHeaderCell>
            <AdminTableHeaderCell>Event</AdminTableHeaderCell>
            <AdminTableHeaderCell>Actor</AdminTableHeaderCell>
            <AdminTableHeaderCell>Target</AdminTableHeaderCell>
            <AdminTableHeaderCell>Outcome</AdminTableHeaderCell>
            <AdminTableHeaderCell>Request ID</AdminTableHeaderCell>
            <AdminTableHeaderCell>Details</AdminTableHeaderCell>
          </AdminTableHeader>
          <tbody>
            {events.map((event) => {
              const isExpanded = expandedRows.has(event.id);
              const integrationCode = getIntegrationCode(event);
              return (
                  <React.Fragment key={event.id}>
                    <AdminTableRow onClick={() => toggleRow(event.id)}>
                      <AdminTableCell>
                        {new Date(event.ts).toLocaleString()}
                      </AdminTableCell>
                      <AdminTableCell>
                        <code style={{ fontFamily: 'var(--font-family-monospace)', fontSize: 'var(--font-size-sm)', backgroundColor: 'var(--color-bg-secondary)', padding: '0.25rem 0.5rem', borderRadius: 'var(--radius-sm)' }}>{event.action}</code>
                      </AdminTableCell>
                      <AdminTableCell>
                        {isActorUUID(event.actor_id) ? (
                          <a
                            href={`/admin/users/${event.actor_id}`}
                            onClick={(e) => {
                              e.preventDefault();
                              navigate(`/admin/users/${event.actor_id}`);
                            }}
                            className="admin-table-action-button"
                          >
                            {event.actor_id}
                          </a>
                        ) : (
                          event.actor_id
                        )}
                      </AdminTableCell>
                      <AdminTableCell>
                        {integrationCode ? (
                          <a
                            href={`/admin/integrations/${integrationCode}`}
                            onClick={(e) => {
                              e.preventDefault();
                              navigate(`/admin/integrations/${integrationCode}`);
                            }}
                            className="admin-table-action-button"
                          >
                            {event.target_type}:{event.target_id}
                          </a>
                        ) : (
                          `${event.target_type}:${event.target_id}`
                        )}
                      </AdminTableCell>
                      <AdminTableCell>
                        <AdminStatusBadge status={event.result === 'success' ? 'active' : event.result === 'failure' ? 'inactive' : 'neutral'}>
                          {event.result}
                        </AdminStatusBadge>
                      </AdminTableCell>
                      <AdminTableCell>
                        {event.request_id ? (
                          <div style={styles.requestIdCell}>
                            <code style={styles.codeSmall}>
                              {event.request_id.substring(0, 8) + '...'}
                            </code>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                copyRequestId(event.request_id);
                              }}
                              style={styles.copyButton}
                              type="button"
                              title="Copy request ID"
                            >
                              Copy
                            </button>
                          </div>
                        ) : (
                          <span>—</span>
                        )}
                      </AdminTableCell>
                      <AdminTableCell>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', flexWrap: 'wrap' }}>
                          <div style={styles.pivotActions}>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                const params = new URLSearchParams();
                                params.set('actor_id', event.actor_id);
                                if (integrationCode) params.set('integration', integrationCode);
                                navigate(`/admin/audit?${params.toString()}`);
                              }}
                              style={styles.pivotButton}
                              type="button"
                              title="Filter by same actor"
                            >
                              Actor
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                const params = new URLSearchParams();
                                params.set('target_id', event.target_id);
                                if (integrationCode) params.set('integration', integrationCode);
                                navigate(`/admin/audit?${params.toString()}`);
                              }}
                              style={styles.pivotButton}
                              type="button"
                              title="Filter by same target"
                            >
                              Target
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                const params = new URLSearchParams();
                                params.set('action', event.action);
                                if (integrationCode) params.set('integration', integrationCode);
                                navigate(`/admin/audit?${params.toString()}`);
                              }}
                              style={styles.pivotButton}
                              type="button"
                              title="Filter by same action"
                            >
                              Action
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate('/admin/audit');
                              }}
                              style={styles.pivotButton}
                              type="button"
                              title="Clear all filters"
                            >
                              Clear
                            </button>
                          </div>
                          {event.request_id && (
                            <a
                              href={`/admin/audit?request_id=${encodeURIComponent(event.request_id)}${integrationCode ? `&integration=${encodeURIComponent(integrationCode)}` : ''}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                navigate(`/admin/audit?request_id=${encodeURIComponent(event.request_id)}${integrationCode ? `&integration=${encodeURIComponent(integrationCode)}` : ''}`);
                              }}
                              style={styles.link}
                            >
                              Related
                            </a>
                          )}
                          <button
                            onClick={() => toggleRow(event.id)}
                            style={styles.toggleButton}
                            type="button"
                          >
                            {isExpanded ? '−' : '+'}
                          </button>
                        </div>
                      </AdminTableCell>
                    </AdminTableRow>
                    {isExpanded && (
                      <AdminTableRow>
                        <td colSpan={7} style={{ padding: 'var(--spacing-4)', backgroundColor: 'var(--color-bg-secondary)' }}>
                          <div style={styles.expandedContent}>
                            <div style={styles.expandedSection}>
                              <strong>Reason:</strong>{' '}
                              {event.reason || '—'}
                            </div>
                            {event.error_summary && (
                              <div style={styles.expandedSection}>
                                <strong>Error:</strong>{' '}
                                <span style={styles.errorText}>{event.error_summary}</span>
                              </div>
                            )}
                            {event.metadata && (
                              <div style={styles.expandedSection}>
                                <strong>Metadata:</strong>
                                <pre style={styles.metadataPre}>
                                  {JSON.stringify(event.metadata, null, 2)}
                                </pre>
                              </div>
                            )}
                          </div>
                        </td>
                      </AdminTableRow>
                    )}
                  </React.Fragment>
                );
              })}
          </tbody>
        </AdminTable>
      )}

      {/* Pagination */}
      {nextCursor && (
        <div style={styles.pagination}>
          <button onClick={handleNextPage} style={styles.button}>
            Load More
          </button>
        </div>
      )}
    </div>
  );
}

const getOutcomeBadgeStyle = (outcome) => {
  const styles = {
    success: { bg: '#d1fae5', color: '#065f46' },
    failure: { bg: '#fee2e2', color: '#991b1b' },
  };
  const style = styles[outcome] || { bg: '#f3f4f6', color: '#374151' };
  return {
    display: 'inline-block',
    padding: '0.25rem 0.75rem',
    borderRadius: '0.25rem',
    fontSize: '0.875rem',
    fontWeight: '500',
    backgroundColor: style.bg,
    color: style.color,
  };
};

const styles = {
  container: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '2rem',
  },
  header: {
    fontSize: '2rem',
    marginBottom: '1.5rem',
    color: '#111827',
  },
  filterBar: {
    backgroundColor: '#fff',
    borderRadius: '0.5rem',
    padding: '1.5rem',
    marginBottom: '1.5rem',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '1rem',
    alignItems: 'end',
  },
  filterGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  label: {
    fontSize: '0.875rem',
    fontWeight: '500',
    color: '#374151',
  },
  input: {
    padding: '0.5rem',
    border: '1px solid #d1d5db',
    borderRadius: '0.375rem',
    fontSize: '0.875rem',
  },
  select: {
    padding: '0.5rem',
    border: '1px solid #d1d5db',
    borderRadius: '0.375rem',
    fontSize: '0.875rem',
  },
  applyButton: {
    padding: '0.5rem 1rem',
    backgroundColor: '#3b82f6',
    color: '#fff',
    border: 'none',
    borderRadius: '0.375rem',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: '500',
    height: 'fit-content',
  },
  tableContainer: {
    backgroundColor: '#fff',
    borderRadius: '0.5rem',
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    padding: '1rem',
    textAlign: 'left',
    backgroundColor: '#f3f4f6',
    fontWeight: '600',
    color: '#374151',
    borderBottom: '2px solid #e5e7eb',
    fontSize: '0.875rem',
  },
  tr: {
    borderBottom: '1px solid #e5e7eb',
  },
  td: {
    padding: '1rem',
    color: '#374151',
    fontSize: '0.875rem',
  },
  code: {
    backgroundColor: '#f3f4f6',
    padding: '0.25rem 0.5rem',
    borderRadius: '0.25rem',
    fontSize: '0.875rem',
    fontFamily: 'monospace',
  },
  codeSmall: {
    backgroundColor: '#f3f4f6',
    padding: '0.125rem 0.25rem',
    borderRadius: '0.25rem',
    fontSize: '0.75rem',
    fontFamily: 'monospace',
  },
  link: {
    color: '#3b82f6',
    textDecoration: 'none',
  },
  toggleButton: {
    padding: '0.25rem 0.5rem',
    border: '1px solid #d1d5db',
    borderRadius: '0.25rem',
    backgroundColor: '#fff',
    cursor: 'pointer',
    fontSize: '0.875rem',
  },
  expandedCell: {
    padding: '1rem',
    backgroundColor: '#f9fafb',
  },
  expandedContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  expandedSection: {
    fontSize: '0.875rem',
  },
  metadataPre: {
    backgroundColor: '#1f2937',
    color: '#f9fafb',
    padding: '1rem',
    borderRadius: '0.375rem',
    overflow: 'auto',
    fontSize: '0.75rem',
    fontFamily: 'monospace',
    margin: 0,
  },
  errorText: {
    color: '#ef4444',
  },
  actionsCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    flexWrap: 'wrap',
  },
  pivotActions: {
    display: 'flex',
    gap: '0.25rem',
    flexWrap: 'wrap',
  },
  pivotButton: {
    padding: '0.25rem 0.5rem',
    border: '1px solid #d1d5db',
    borderRadius: '0.25rem',
    backgroundColor: '#fff',
    color: '#374151',
    cursor: 'pointer',
    fontSize: '0.75rem',
    fontWeight: '500',
  },
  emptyState: {
    textAlign: 'center',
    padding: '2rem',
    color: '#6b7280',
  },
  pagination: {
    marginTop: '1.5rem',
    display: 'flex',
    justifyContent: 'center',
  },
  button: {
    padding: '0.5rem 1rem',
    backgroundColor: '#3b82f6',
    color: '#fff',
    border: 'none',
    borderRadius: '0.375rem',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: '500',
  },
  error: {
    padding: '2rem',
    backgroundColor: '#fee2e2',
    color: '#991b1b',
    borderRadius: '0.5rem',
  },
};
